/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { AbstractControl, FormArray, FormGroup } from '@angular/forms';
import {
  AppControl,
  AppFormArray,
  AppFormGroup, controlPackedValue, ControlSchemaPackedValue,
  ControlSchemaType,
  FormArraySchema,
  FormGroupSchema,
  toAppFormControl,
} from '@gh/common-shared';
import { getControl } from '@gh/core-ui';
import { arrayDiff, objectDiffKeys } from '@gh/core-util';
import { noop } from 'lodash';

export interface DiffFormResult {
  onNew?(control: AbstractControl): void;

  onUpdate?(control: AbstractControl): void;

  onRemove?(control: AbstractControl): void;
}

export function diffFormGroup(schema?: FormGroupSchema,
                              group?: AppFormGroup,
                              packedValue?: ControlSchemaPackedValue): ProcedureFn<DiffFormResult> {
  if (group && schema) {
    if ((group instanceof FormGroup) !== (schema.schemaType === ControlSchemaType.Group
      || schema.schemaType === ControlSchemaType.Root)) {
      throw errorControlTypeHasChanged();
    }
    const { children } = schema;
    objectDiffKeys(group.controls, children, {
      onNew: (key) => {
        const value = packedValue && packedValue.value ? packedValue.value[key] : void 0;
        group.setControl(key, children[key].build(value));
      },
      onIdentity: (key) => {
        const value = packedValue && packedValue.value ? controlPackedValue(packedValue.value[key]) : void 0;
        const control = <AppControl>getControl(group, key);
        control.updateSchema(children[key], value);
      },
      onRemove: (key) => group.removeControl(key),
    });
    return ({ onUpdate }) => onUpdate && onUpdate(group);
  } else if (schema) {
    return ({ onNew }) => onNew && onNew(schema.build(packedValue ? packedValue.value : void 0));
  } else if (group) {
    return ({ onRemove }) => onRemove && onRemove(group);
  } else {
    return noop;
  }
}

export function diffFormArray(schema?: FormArraySchema,
                              array?: AppFormArray,
                              packedValue?: ControlSchemaPackedValue): ProcedureFn<DiffFormResult> {
  if (array && schema) {
    if ((array instanceof FormArray) !== (schema.schemaType === ControlSchemaType.Array)) {
      throw errorControlTypeHasChanged();
    }
    const { children } = schema;
    arrayDiff(array.controls, children, 'key', {
      onInsert: (control: AppControl, itemSchema, newIndex) => {
        const value = packedValue && packedValue.value ? controlPackedValue(packedValue.value[newIndex]) : void 0;
        if (control) {
          control.updateSchema(itemSchema, value);
        }
        array.insert(newIndex, control || itemSchema.build(value ? value.value : void 0));
      },
      onIdentity: (control, itemSchema, index) => {
        const value = packedValue && packedValue.value ? controlPackedValue(packedValue.value[index]) : void 0;
        toAppFormControl(control).updateSchema(itemSchema, value);
      },
      onDelete: (_, itemSchema, oldIndex) => array.removeAt(oldIndex),
    });
    return ({ onUpdate }) => onUpdate && onUpdate(array);
  } else if (schema) {
    return ({ onNew }) => onNew && onNew(schema.build(packedValue ? packedValue.value : void 0));
  } else if (array) {
    return ({ onRemove }) => onRemove && onRemove(array);
  } else {
    return noop;
  }
}

export function errorControlTypeHasChanged(): Error {
  return new Error('Control type has changed');
}
